import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';

import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import {
  ListenStruktur,
  Messstelle,
  MessstellenListeDetail,
  StrukturAenderungAbgelehnt,
  pruefeStruktur,
  strukturSpalten
} from '../../../../../Models/messstelle.model';
import { InbetriebnahmeService } from '../../../../../Services/inbetriebnahme.service';


export interface StrukturBearbeitenDialogData {
  liste: MessstellenListeDetail;
}


/**
 * Struktur einer bestehenden, manuell erstellten Liste anpassen.
 *
 * Spalten hängen an ihrer stabilen Id: Umbenennen behält alle Werte, neue
 * Spalten kommen leer hinzu, die Reihenfolge ist frei. Eine Spalte mit
 * gespeicherten Werten verschwindet erst nach ausdrücklicher Bestätigung –
 * der Server antwortet dafür mit 409 und nennt die betroffenen Spalten. Ein
 * Wechsel der Kennzeichnungsspalte wird serverseitig auf Vollständigkeit und
 * Eindeutigkeit geprüft; Prüfpunkte und Prüfergebnisse bleiben erhalten.
 *
 * Liefert die frisch geladene Liste oder null (abgebrochen).
 */
@Component({
  selector: 'app-struktur-bearbeiten-dialog',
  template: `
    <h2 mat-dialog-title>Spaltenstruktur bearbeiten</h2>

    <mat-dialog-content>
      <p class="hinweis">
        <strong>{{ data.liste.name }}</strong> · {{ data.liste.messstellenCount }} Messstelle{{ data.liste.messstellenCount === 1 ? '' : 'n' }}.
        Umbenennen behält alle Werte. Spalten mit Werten werden nur nach Bestätigung gelöscht.
        Prüfungen und Prüfergebnisse bleiben unverändert.
      </p>

      <app-struktur-editor [struktur]="struktur" [disabled]="speichert" [spaltenMitWerten]="spaltenMitWerten"
        (strukturChange)="geaendert($event)" (gueltigChange)="gueltig = $event">
      </app-struktur-editor>

      <div class="fehler" *ngIf="fehler">
        <mat-icon inline>error_outline</mat-icon>
        <div>
          <div>{{ fehler }}</div>
          <ul *ngIf="probleme.length > 0">
            <li *ngFor="let p of probleme">{{ p }}</li>
          </ul>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="abbrechen()" [disabled]="speichert">Abbrechen</button>
      <button mat-raised-button color="primary" type="button" (click)="speichern()" [disabled]="!kannSpeichern">
        <mat-icon>save</mat-icon>
        Struktur speichern
      </button>
      <mat-spinner diameter="20" *ngIf="speichert"></mat-spinner>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { width: 860px; max-width: 100%; }
    .hinweis { margin: 0 0 var(--space-4); font-size: var(--fs-base); line-height: 1.5; color: var(--text-secondary); }
    .fehler {
      display: flex; gap: var(--space-2); align-items: flex-start;
      margin-top: var(--space-3); padding: 10px 12px;
      border: 1px solid var(--status-error-border); border-radius: var(--radius-sm);
      background: var(--status-error-bg); color: var(--status-error-text);
      font-size: var(--fs-base); line-height: 1.5;
    }
    .fehler ul { margin: 6px 0 0; padding-left: 18px; }
    mat-dialog-actions mat-spinner { margin-left: 8px; }
  `]
})
export class StrukturBearbeitenDialogComponent {

  struktur: ListenStruktur;
  gueltig: boolean;

  /** Spalten-Id -> wie viele Messstellen dort einen Wert haben. Für den Hinweis im Editor. */
  spaltenMitWerten: { [spaltenId: string]: number } = {};

  speichert = false;
  fehler = '';
  probleme: string[] = [];

  private aktuelle: ListenStruktur;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: StrukturBearbeitenDialogData,
    private dialogRef: MatDialogRef<StrukturBearbeitenDialogComponent, MessstellenListeDetail | null>,
    private service: InbetriebnahmeService,
    private dialog: MatDialog
  ) {
    const quelle = data.liste.struktur ?? { spalten: [] };

    // Kopie – der Editor darf die angezeigte Liste nicht verändern.
    this.struktur = { spalten: strukturSpalten(quelle).map(s => ({ ...s })) };
    this.aktuelle = this.struktur;
    this.gueltig = pruefeStruktur(this.struktur).length === 0;

    this.spaltenMitWerten = StrukturBearbeitenDialogComponent.zaehleWerte(quelle, data.liste.messstellen ?? []);
  }


  private static zaehleWerte(struktur: ListenStruktur, messstellen: Messstelle[]): { [id: string]: number } {
    const ergebnis: { [id: string]: number } = {};

    for (const spalte of struktur.spalten) {
      let anzahl = 0;

      for (const m of messstellen) {
        const wert = m.values?.[spalte.name];
        if (wert != null && String(wert).trim().length > 0) {
          anzahl++;
        }
      }

      ergebnis[spalte.id] = anzahl;
    }

    return ergebnis;
  }


  geaendert(struktur: ListenStruktur): void {
    this.aktuelle = struktur;
    this.fehler = '';
    this.probleme = [];
  }


  get kannSpeichern(): boolean {
    return !this.speichert && this.gueltig && pruefeStruktur(this.aktuelle).length === 0;
  }


  speichern(bestaetigt: string[] = []): void {

    if (!this.kannSpeichern) {
      return;
    }

    this.speichert = true;
    this.fehler = '';
    this.probleme = [];

    this.service.updateStruktur(this.data.liste.id, this.aktuelle, bestaetigt).subscribe({
      next: liste => {
        this.speichert = false;
        this.dialogRef.close(liste);
      },
      error: (err: HttpErrorResponse) => {
        this.speichert = false;

        const abgelehnt = err.status === 409 ? err.error as StrukturAenderungAbgelehnt : null;

        if (abgelehnt?.loeschungBestaetigen?.length) {
          this.bestaetigeLoeschung(abgelehnt, bestaetigt);
          return;
        }

        if (abgelehnt?.kennzeichnungProbleme?.length) {
          this.fehler = abgelehnt.message || 'Die Kennzeichnung wäre nach dieser Änderung nicht eindeutig.';
          this.probleme = abgelehnt.kennzeichnungProbleme;
          return;
        }

        this.fehler = err.error?.message ?? 'Die Struktur konnte nicht gespeichert werden.';
      }
    });
  }


  /** Spalten mit Werten werden nur nach ausdrücklicher Bestätigung gelöscht. */
  private bestaetigeLoeschung(abgelehnt: StrukturAenderungAbgelehnt, bisher: string[]): void {

    const details = abgelehnt.loeschungBestaetigen.map(s =>
      `„${s.name}“ – ${s.anzahlWerte} gespeicherte${s.anzahlWerte === 1 ? 'r Wert' : ' Werte'}`);

    const data: ConfirmDialogData = {
      title: 'Spalten mit Werten löschen?',
      message: 'Die folgenden Spalten enthalten gespeicherte Werte. Beim Entfernen gehen diese Werte unwiderruflich verloren.',
      details,
      warning: 'Prüfpunkte, Prüfergebnisse, Bilder und Bemerkungen der Messstellen bleiben erhalten – nur die Werte dieser Spalten werden gelöscht.',
      confirmText: 'Spalten und Werte löschen'
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, maxWidth: '560px' })
      .afterClosed()
      .subscribe(ok => {

        if (!ok) {
          this.fehler = 'Die Struktur wurde nicht gespeichert. Entfernte Spalten mit Werten brauchen eine Bestätigung.';
          return;
        }

        const ids = Array.from(new Set([...bisher, ...abgelehnt.loeschungBestaetigen.map(s => s.id)]));

        this.speichern(ids);
      });
  }


  abbrechen(): void {
    if (!this.speichert) {
      this.dialogRef.close(null);
    }
  }
}
